import React, { useState, useMemo, useEffect } from "react";
import "./AdminWeeklyPlan.css";
import bfLogo from "../assets/bf_logo.png";
import { 
  Search, ArrowUpDown, ChevronLeft, ChevronRight, 
  FileText, Edit3, Eye, Banknote, Trash2, AlertCircle,
  Lock, Unlock 
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* ===== KOMPONENTAI ===== */
import StatusModal from "../components/StatusModal";
import BudgetModal from "../components/BudgetModal";
import ProjectViewModal from "../components/ProjectViewModal";
import DeletionModal from "../components/DeletionModal";

/* ===== FIREBASE ===== */
import { db, auth } from "../firebase";
import { 
  collection, getDocs, doc, setDoc, deleteDoc, 
  query, where, onSnapshot, writeBatch, getDoc, 
  deleteField 
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const STATUS_MAP = {
  darbas:     { label: "darbas",    i18nKey: "work",     color: "#00ff88" },
  laisva:     { label: "laisva",    i18nKey: "off",      color: "#ff7b00" }, 
  serga:      { label: "serga",     i18nKey: "sick",     color: "#ffd400" },
  atostogos:  { label: "atostogos", i18nKey: "vacation", color: "#00b7ff" },
  svente:     { label: "šventė",    i18nKey: "holiday",  color: "#b366ff" },
  reset:      null
};

const DAYS = ["Mo","Di","Mi","Do","Fr","Sa","So"];
const DAYS_MAP = { Mo: "monday", Di: "tuesday", Mi: "wednesday", Do: "thursday", Fr: "friday", Sa: "saturday", So: "sunday" };

/* --- PAGALBINĖS FUNKCIJOS --- */
const getFormattedDate = (year, kw, dayIndex) => {
  const firstDayOfYear = new Date(year, 0, 1);
  const days = (kw - 1) * 7;
  const date = new Date(year, 0, firstDayOfYear.getDate() + days - firstDayOfYear.getDay() + 1 + dayIndex);
  return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
};

const getWeekDates = (year, kw) => {
  const firstDayOfYear = new Date(year, 0, 1);
  const days = (kw - 1) * 7;
  const start = new Date(year, 0, firstDayOfYear.getDate() + days - firstDayOfYear.getDay() + 1);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  return `${fmt(start)} - ${fmt(end)}.${year}`;
};

const getCurrentWeek = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

// Standartinis formatas be nulių (pvz "5", "12")
const getDayDetail = (year, kw, dayIndex) => {
  const firstDayOfYear = new Date(year, 0, 1);
  const days = (kw - 1) * 7;
  const date = new Date(year, 0, firstDayOfYear.getDate() + days - firstDayOfYear.getDay() + 1 + dayIndex);
  return { 
      year: date.getFullYear(), 
      month: (date.getMonth() + 1).toString().padStart(2, '0'), 
      day: date.getDate().toString() 
  };
};

const hexToRgba = (hex, a) => {
  if (!hex) return `rgba(255,255,255,${a})`;
  const n = parseInt(hex.replace("#",""),16);
  return `rgba(${(n>>16)&255},${(n>>8)&255},${n&255},${a})`;
};

export default function AdminWeeklyPlan() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  /* --- BŪSENOS --- */
  const [workers, setWorkers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");
  const [kw, setKw] = useState(getCurrentWeek());
  
  const [dayStatuses, setDayStatuses] = useState({});
  const [dbStatuses, setDbStatuses] = useState({}); 
  
  const [pendingStatus, setPendingStatus] = useState(null);
  const [applyMode, setApplyMode] = useState("status");
  const [projectDone, setProjectDone] = useState(false);
  
  const [sentProjects, setSentProjects] = useState([]); 
  
  const [creating, setCreating] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);

  /* --- MODALAI --- */
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [deletionData, setDeletionData] = useState(null);
  const [warningMsg, setWarningMsg] = useState(null); 
  
  const [viewProject, setViewProject] = useState(null);
  
  const [targetKw, setTargetKw] = useState("");
  const [copyWorkers, setCopyWorkers] = useState(false);
  const [individualEdits, setIndividualEdits] = useState({});
  const [isModalEditMode, setIsModalEditMode] = useState(false);

  const [info, setInfo] = useState({
    auftraggeber: "", bauvorhaben: "", adresse: "", ansprechpartner: "",
    kontakt: "", unterkunft: "", tel: "", transport: "", kommentar: "",
    projektwert: "", budgetDetails: []
  });

  /* --- DATA LOADING --- */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (userSnap.exists()) setUserData(userSnap.data());
      } else { navigate("/admin/login"); }
    });
    return () => unsubscribe();
  }, [navigate]);

  const masterId = userData?.belongsTo || currentUser?.uid;

  useEffect(() => {
    if (!masterId) return;
    const q = query(collection(db, "workers"), where("adminId", "==", masterId));
    getDocs(q).then(snap => {
        const activeWorkers = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(w => !w.archived)
            .map(w => ({ id: w.id, name: w.name }));
        setWorkers(activeWorkers);
    });
  }, [masterId]);

  // --- KLAUSOMĖS DUOMENŲ ---
  useEffect(() => {
    if (!masterId) return;
    
    // 1. Statusai
    const qStatuses = query(collection(db, "weeklyPlans"), where("adminId", "==", masterId), where("year", "==", year), where("kw", "==", kw));
    const unsubscribeStatuses = onSnapshot(qStatuses, (snap) => {
        if (editingProjectId) return; // Jei redaguojam, neliesti
        
        const next = {};
        const nextDb = {};
        snap.docs.forEach(d => {
            const data = d.data();
            const st = Object.values(STATUS_MAP).find(s => s?.label === data.status) || { label: data.status, color: data.color, i18nKey: null };
            if (st) {
                if (!next[data.workerId]) next[data.workerId] = {};
                next[data.workerId][data.dayIndex] = st;
                
                if (!nextDb[data.workerId]) nextDb[data.workerId] = {};
                nextDb[data.workerId][data.dayIndex] = data.status;
            }
        });
        setDayStatuses(next);
        setDbStatuses(nextDb);
    });

    // 2. Projektai
    const qProjects = query(collection(db, "projects"), where("adminId", "==", masterId), where("kw", "==", kw), where("year", "==", year));
    const unsubscribeProjects = onSnapshot(qProjects, (snap) => {
        setSentProjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => { unsubscribeStatuses(); unsubscribeProjects(); };
  }, [kw, year, masterId, editingProjectId]);


  /* ===== FUNKCIJOS ===== */

  const handleEditProject = (project) => {
    setEditingProjectId(project.id);
    setInfo(project.info);
    
    const nextStatuses = JSON.parse(JSON.stringify(dayStatuses));
    const nextDbStatuses = JSON.parse(JSON.stringify(dbStatuses));

    Object.entries(project.days || {}).forEach(([dayKey, workersArray]) => {
      let dayIndex = DAYS.indexOf(dayKey);
      if (dayIndex === -1) {
          const mappedKey = Object.keys(DAYS_MAP).find(k => DAYS_MAP[k] === dayKey.toLowerCase());
          if (mappedKey) dayIndex = DAYS.indexOf(mappedKey);
      }
      if (dayIndex === -1) return;

      workersArray.forEach(worker => {
        const wId = typeof worker === 'string' ? worker : worker.id;
        if (!nextStatuses[wId]) nextStatuses[wId] = {};
        nextStatuses[wId][dayIndex] = STATUS_MAP.darbas;
        
        // Pasižymim kad tai yra DB, kad veiktų trynimas
        if (!nextDbStatuses[wId]) nextDbStatuses[wId] = {};
        nextDbStatuses[wId][dayIndex] = "darbas";
      });
    });

    setDayStatuses(nextStatuses);
    setDbStatuses(nextDbStatuses);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingProjectId(null);
    setInfo({ auftraggeber: "", bauvorhaben: "", adresse: "", ansprechpartner: "", kontakt: "", unterkunft: "", tel: "", transport: "", kommentar: "", projektwert: "", budgetDetails: [] });
  };

  const handleInfoKeyDown = (e, index) => {
    const inputs = document.querySelectorAll('.weekly-info-box input, .weekly-info-box textarea');
    if (e.key === "Enter" || e.key === "ArrowDown") { e.preventDefault(); if (index + 1 < inputs.length) inputs[index + 1].focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); if (index - 1 >= 0) inputs[index - 1].focus(); }
  };

  const getWorkerProjectsForDay = (workerId, dayIndex) => {
    const dayName = DAYS[dayIndex];
    return sentProjects.filter(p => p.days?.[dayName]?.some(w => (typeof w === 'string' ? w === workerId : w.id === workerId)));
  };

  const getAssignedProjectNames = (workerId, dayIndex) => {
    const projects = getWorkerProjectsForDay(workerId, dayIndex);
    const visibleProjects = editingProjectId 
      ? projects.filter(p => p.id !== editingProjectId) 
      : projects;
      
    return visibleProjects.length > 0 
      ? visibleProjects.map(p => p.info.bauvorhaben).join(" / ").substring(0, 40) 
      : null;
  };

  const handleCellClick = (workerId, dayIndex) => {
    // 1. TRYNIMAS
    if (applyMode === "reset") {
      if (editingProjectId) {
         setDayStatuses(prev => {
            const n = { ...prev };
            if (!n[workerId]) n[workerId] = {};
            n[workerId][dayIndex] = { label: "DELETED", color: "transparent" };
            return n;
         });
         return;
      }
      
      const projects = getWorkerProjectsForDay(workerId, dayIndex);
      if (projects.length > 0) {
        setDeletionData({ workerId, dayIndex, projects, workerName: workers.find(w => w.id === workerId)?.name });
        setShowDeletionModal(true);
        return;
      }
      
      setDayStatuses(prev => {
        const n = { ...prev };
        if (!n[workerId]) n[workerId] = {};
        n[workerId][dayIndex] = { label: "DELETED", color: "transparent" };
        return n;
      });
      return;
    }
    
    // 2. STATUSAS
    if (pendingStatus) {
      setDayStatuses(prev => {
        const currentStatus = prev[workerId]?.[dayIndex];
        if (currentStatus?.label === pendingStatus.label) {
             const n = { ...prev };
             n[workerId][dayIndex] = { label: "DELETED", color: "transparent" };
             return n;
        }
        return { ...prev, [workerId]: { ...(prev[workerId] || {}), [dayIndex]: pendingStatus } };
      });
      return;
    }

    setShowStatusModal(true);
  };

  const hasUnsavedChanges = useMemo(() => {
    const localChanges = Object.entries(dayStatuses).some(([wid, map]) => 
        Object.entries(map).some(([d, s]) => {
            if (s.label === "DELETED") return true;
            const dbVal = dbStatuses[wid]?.[d];
            if (!dbVal) return true; 
            if (s.label !== dbVal) return true;
            return false;
        })
    );
    if (localChanges) return true;
    const dbDeletions = Object.entries(dbStatuses).some(([wid, map]) => 
        Object.entries(map).some(([d, val]) => {
            if (!dayStatuses[wid]?.[d]) return true;
            return false;
        })
    );
    return dbDeletions;
  }, [dayStatuses, dbStatuses]);


  const handleFinishProject = async () => {
    if (!hasUnsavedChanges && !editingProjectId) {
        setWarningMsg(t('no_new_changes') || "Nėra naujų pakeitimų!");
        return;
    }

    setCreating(true);
    const batch = writeBatch(db);
    
    try {
        let projectId = editingProjectId || Date.now().toString();
        let payload = {};
        DAYS.forEach(d => { payload[d] = []; });
        
        let existingWorkerDetails = {};
        // Čia saugosime ID tų žmonių, kurie JAU BUVO projekte prieš redagavimą
        let originallyActiveWorkers = new Set(); 
        
        if (editingProjectId) {
             const pDoc = await getDoc(doc(db, "projects", editingProjectId));
             if (pDoc.exists()) {
                 const pData = pDoc.data();
                 existingWorkerDetails = pData.workerDetails || {};
                 
                 // Surenkame visus aktyvius darbuotojus į vieną krūvą patikrinimui
                 if(pData.days) {
                    Object.values(pData.days).forEach(list => {
                        list.forEach(w => originallyActiveWorkers.add(typeof w === 'string' ? w : w.id));
                    });
                    // Kopijuojame senus sąrašus
                    Object.keys(payload).forEach(k => {
                        if(pData.days[k]) payload[k] = pData.days[k];
                    });
                 }
             }
        }

        // --- CIKLAS PER VISUS PAKEITIMUS ---
        for (const [wid, map] of Object.entries(dayStatuses)) {
            for (const [d, s] of Object.entries(map || {})) {
                if (!s) continue;
                const dayIndex = parseInt(d);
                const dateInfo = getDayDetail(year, kw, dayIndex);
                const docId = `${wid}_${dateInfo.year}_${dateInfo.month}`;
                
                const employeeRef = doc(db, "monthlyWorkHours", docId);
                const adminOnlyRef = doc(db, "adminWorkerWorkHours", docId);
                const weeklyPlanRef = doc(db, "weeklyPlans", `${year}_KW${kw}_${wid}_${d}`);
                const dayName = DAYS[d];

                // --- TRYNIMAS ---
                if (s.label === "DELETED") {
                    if (editingProjectId) {
                        payload[dayName] = payload[dayName].filter(w => (typeof w === 'string' ? w !== wid : w.id !== wid));
                    }
                    batch.delete(weeklyPlanRef);
                    batch.set(employeeRef, { days: { [dateInfo.day]: deleteField() } }, { merge: true });
                    batch.set(adminOnlyRef, { days: { [dateInfo.day]: deleteField() } }, { merge: true });
                    continue; 
                }

                // --- DARBAS (Į PROJEKTĄ) ---
                if (s.label === "darbas") {
                    if ((info.bauvorhaben && info.adresse) || editingProjectId) {
                        const wObj = workers.find(w => w.id === wid);
                        if (wObj) {
                            // Į sąrašą
                            const exists = payload[dayName].some(w => (typeof w === 'string' ? w === wid : w.id === wid));
                            if (!exists) {
                                payload[dayName].push({ id: wid, name: wObj.name });
                            }
                            batch.delete(weeklyPlanRef);
                            
                            // --- PAGRINDINIS PATAISYMAS DĖL INFO ---
                            // Ar šis darbuotojas buvo aktyvus projekte prieš redagavimą?
                            const wasActive = originallyActiveWorkers.has(wid);
                            
                            // Jei NEBUVO aktyvus (t.y. pridedamas dabar) - PRIVERSTINAI atnaujinam info
                            // Jei BUVO aktyvus - tikrinam ar turi info. Jei neturi - užpildom.
                            
                            if (!wasActive || !existingWorkerDetails[wid]) {
                                // NAUJOKAS: Visada imame info iš viršutinių laukelių
                                existingWorkerDetails[wid] = { 
                                    accommodation: info.unterkunft, 
                                    transport: info.transport, 
                                    phone: info.tel, 
                                    contact: info.kontakt, 
                                    comment: info.kommentar 
                                };
                            } else {
                                // SENBUVIS: Saugome seną info, nebent ji tuščia
                                const old = existingWorkerDetails[wid];
                                if (!old.accommodation && !old.transport) {
                                     existingWorkerDetails[wid] = { 
                                        accommodation: info.unterkunft, 
                                        transport: info.transport,
                                        phone: info.tel, 
                                        contact: info.kontakt, 
                                        comment: info.kommentar 
                                     };
                                }
                            }

                            const finalDet = existingWorkerDetails[wid];

                            // ĮRAŠOME Į DARBUOTOJO FAILĄ (TELEFONUI)
                            batch.set(employeeRef, { 
                                adminId: masterId, 
                                days: { 
                                    [dateInfo.day]: { 
                                        status: "darbas", 
                                        projectId: projectId, 
                                        projectName: info.bauvorhaben, 
                                        address: info.adresse, 
                                        hotel: finalDet.accommodation, 
                                        transport: finalDet.transport, 
                                        phone: finalDet.phone,
                                        contact: finalDet.contact,
                                        comment: finalDet.comment,
                                        updatedAt: Date.now() 
                                    } 
                                } 
                            }, { merge: true });
                            
                            batch.set(adminOnlyRef, { adminId: masterId, days: { [dateInfo.day]: deleteField() } }, { merge: true });
                        }
                    }
                } 
                // --- KITI STATUSAI ---
                else {
                    batch.set(weeklyPlanRef, { 
                        adminId: masterId, workerId: wid, dayIndex, kw, year, 
                        status: s.label, color: s.color, updatedAt: Date.now() 
                    });
                    
                    batch.set(adminOnlyRef, { 
                        adminId: masterId, 
                        days: { [dateInfo.day]: { status: s.label, color: s.color, isStatusOnly: true, updatedAt: Date.now() } } 
                    }, { merge: true });

                    batch.set(employeeRef, { 
                        adminId: masterId, 
                        days: { [dateInfo.day]: { status: s.label, objekt: "", isStatusOnly: true, updatedAt: Date.now() } } 
                    }, { merge: true });
                }
            }
        }

        // Išvalymas (db skirtumai)
        for (const [wid, map] of Object.entries(dbStatuses)) {
            for (const [d, dbVal] of Object.entries(map || {})) {
                if (!dayStatuses[wid]?.[d]) {
                    const dayIndex = parseInt(d);
                    const dateInfo = getDayDetail(year, kw, dayIndex);
                    const docId = `${wid}_${dateInfo.year}_${dateInfo.month}`;

                    const weeklyPlanRef = doc(db, "weeklyPlans", `${year}_KW${kw}_${wid}_${d}`);
                    const employeeRef = doc(db, "monthlyWorkHours", docId);
                    const adminOnlyRef = doc(db, "adminWorkerWorkHours", docId);

                    batch.delete(weeklyPlanRef);
                    batch.set(employeeRef, { days: { [dateInfo.day]: deleteField() } }, { merge: true });
                    batch.set(adminOnlyRef, { days: { [dateInfo.day]: deleteField() } }, { merge: true });
                }
            }
        }

        if ((info.bauvorhaben && info.adresse) || editingProjectId) {
             await setDoc(doc(db, "projects", projectId), { 
                  adminId: masterId, kw, year, info, days: payload, 
                  workerDetails: existingWorkerDetails, 
                  updatedAt: Date.now() 
             }, { merge: true });
        }

        await batch.commit();
        
        setPendingStatus(null); 
        setApplyMode("status"); 
        setProjectDone(true); 
        setEditingProjectId(null); 
        setInfo({ auftraggeber: "", bauvorhaben: "", adresse: "", ansprechpartner: "", kontakt: "", unterkunft: "", tel: "", transport: "", kommentar: "", projektwert: "", budgetDetails: [] });
        
        setTimeout(() => setProjectDone(false), 2500);

    } catch (err) { alert("Klaida: " + err.message); }
    setCreating(false);
  };

  const handleRemoveFromProject = async (projectId) => {
    const { workerId, dayIndex } = deletionData;
    const dayName = DAYS[dayIndex];
    try {
        const pDoc = await getDoc(doc(db, "projects", projectId));
        if (pDoc.exists()) {
            const pData = pDoc.data();
            const currentList = pData.days[dayName] || [];
            const newList = currentList.filter(w => (typeof w === 'string' ? w !== workerId : w.id !== workerId));
            
            await setDoc(doc(db, "projects", projectId), {
                days: { [dayName]: newList }
            }, { merge: true });
            
            const dateInfo = getDayDetail(year, kw, dayIndex);
            const docId = `${workerId}_${dateInfo.year}_${dateInfo.month}`;
            await setDoc(doc(db, "monthlyWorkHours", docId), {
                 days: { [dateInfo.day]: deleteField() }
            }, { merge: true });
        }
    } catch (e) {
        console.error("Klaida trinant:", e);
    }
    setDayStatuses(prev => {
        const n = { ...prev };
        if (!n[workerId]) n[workerId] = {};
        n[workerId][dayIndex] = { label: "DELETED", color: "transparent" };
        return n;
    });
    setShowDeletionModal(false);
    setDeletionData(null);
  };

  const handleIndividualChange = (workerId, field, value) => {
    setIndividualEdits(prev => ({ ...prev, [workerId]: { ...prev[workerId], [field]: value } }));
  };

  const saveIndividualChanges = async () => {
    if (!viewProject) return;
    try {
      const batch = writeBatch(db);
      const updates = {};
      Object.entries(individualEdits).forEach(([wId, edits]) => {
          if (edits.accommodation !== undefined) updates[`workerDetails.${wId}.accommodation`] = edits.accommodation;
          if (edits.transport !== undefined) updates[`workerDetails.${wId}.transport`] = edits.transport;
          if (edits.phone !== undefined) updates[`workerDetails.${wId}.phone`] = edits.phone;
          if (edits.contact !== undefined) updates[`workerDetails.${wId}.contact`] = edits.contact;
          if (edits.comment !== undefined) updates[`workerDetails.${wId}.comment`] = edits.comment;
      });
      batch.update(doc(db, "projects", viewProject.id), updates);
      await batch.commit();

      setViewProject(prev => {
          const newState = { ...prev };
          if (!newState.workerDetails) newState.workerDetails = {};
          Object.entries(individualEdits).forEach(([wId, edits]) => {
              newState.workerDetails[wId] = { ...(newState.workerDetails[wId] || {}), ...edits };
          });
          return newState;
      });

      setIndividualEdits({});
      setIsModalEditMode(false);
    } catch (err) { console.error(err); alert(t('error')); }
  };

  const handleCopyToWeek = async () => {
    if (!targetKw || targetKw < 1 || targetKw > 53) { alert(t('error')); return; }
    const newKw = parseInt(targetKw);
    const newProjectId = Date.now().toString();
    const newDays = copyWorkers ? { ...viewProject.days } : {};
    const newWorkerDetails = copyWorkers ? { ...viewProject.workerDetails } : {};
    try {
      await setDoc(doc(db, "projects", newProjectId), {
          adminId: masterId,
          kw: newKw,
          year: year,
          info: viewProject.info,
          days: newDays,
          workerDetails: newWorkerDetails,
          updatedAt: Date.now()
      });
      setShowViewModal(false);
      setKw(newKw);
    } catch (error) {
      console.error(error);
      alert(t('error'));
    }
  };

  const filtered = useMemo(() => workers.filter(w => w.name.toLowerCase().includes(search.toLowerCase())).sort((a, b) => sortDir === "asc" ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)), [workers, sortDir, search]);

  return (
    <div className="weekly-container">
      <button className="back-btn" onClick={() => navigate("/admin")}>← {t('back')}</button>
      <div className="weekly-content">
        <img src={bfLogo} className="weekly-logo" alt="BF" />
        <h1 className="weekly-title">{editingProjectId ? `${t('editing')}: ${info.bauvorhaben}` : t('weekly_plan')}</h1>

        <div className="weekly-info-box">
          <div className="info-col">
            <div className="input-group"><label className="input-label">{t('client')}</label><input value={info.auftraggeber} onChange={e => setInfo({ ...info, auftraggeber: e.target.value })} onKeyDown={e => handleInfoKeyDown(e, 0)} /></div>
            <div className="input-group"><label className="input-label">{t('project')}</label><input value={info.bauvorhaben} onChange={e => setInfo({ ...info, bauvorhaben: e.target.value })} onKeyDown={e => handleInfoKeyDown(e, 1)} /></div>
            <div className="input-group"><label className="input-label">{t('address')}</label><input value={info.adresse} onChange={e => setInfo({ ...info, adresse: e.target.value })} onKeyDown={e => handleInfoKeyDown(e, 2)} /></div>
            <div className="input-group"><label className="input-label">{t('contact_person')}</label><input value={info.ansprechpartner} onChange={e => setInfo({ ...info, ansprechpartner: e.target.value })} onKeyDown={e => handleInfoKeyDown(e, 3)} /></div>
            <div className="input-group"><label className="input-label">{t('contact')}</label><input value={info.kontakt} onChange={e => setInfo({ ...info, kontakt: e.target.value })} onKeyDown={e => handleInfoKeyDown(e, 4)} /></div>
            <div className="budget-btn-container">
                <label className="input-label">{t('project_value')}</label>
                <button className="budget-trigger-btn" onClick={() => setShowBudgetModal(true)}>
                    <div className="budget-content"><Banknote size={20} className="budget-icon"/><span className="budget-value">{info.projektwert || t('plan_budget')}</span></div><div className="budget-glow"></div>
                </button>
            </div>
          </div>
          <div className="info-col">
            <div className="input-group"><label className="input-label">{t('accommodation')}</label><input value={info.unterkunft} onChange={e => setInfo({ ...info, unterkunft: e.target.value })} onKeyDown={e => handleInfoKeyDown(e, 5)} /></div>
            <div className="input-group"><label className="input-label">{t('phone')}</label><input value={info.tel} onChange={e => setInfo({ ...info, tel: e.target.value })} onKeyDown={e => handleInfoKeyDown(e, 6)} /></div>
            <div className="input-group"><label className="input-label">{t('transport')}</label><input value={info.transport} onChange={e => setInfo({ ...info, transport: e.target.value })} onKeyDown={e => handleInfoKeyDown(e, 7)} /></div>
            <div className="input-group"><label className="input-label">{t('comment')}</label><textarea value={info.kommentar} onChange={e => setInfo({ ...info, kommentar: e.target.value })} onKeyDown={e => handleInfoKeyDown(e, 8)} /></div>
          </div>
        </div>

        <div className="weekly-topbar">
          <div className="top-btn search-pill"><Search size={16}/><input placeholder={t('search')} value={search} onChange={e => setSearch(e.target.value)} /></div>
          
          <button 
            className="top-btn" 
            style={{
              boxShadow: `0 0 15px ${pendingStatus ? pendingStatus.color : (applyMode === "reset" ? "#ff3b3b" : "rgba(100, 100, 100, 0.5)")}`,
              borderColor: pendingStatus ? pendingStatus.color : (applyMode === "reset" ? "#ff3b3b" : "#444"),
              color: "white",
              transition: "all 0.3s ease"
            }}
            onClick={() => {
              if (applyMode === "reset" || pendingStatus) {
                setApplyMode("status");
                setPendingStatus(null);
              } else {
                setShowStatusModal(true);
              }
            }}
          >
            {pendingStatus 
              ? (pendingStatus.i18nKey ? t(pendingStatus.i18nKey) : pendingStatus.label) 
              : (applyMode === "reset" ? t('delete') : (t('select_status') || "Status auswählen"))
            }
          </button>

          <div className="top-btn blue">
            <ChevronLeft className="nav-arrow" onClick={() => setKw(k => k - 1)} /> {getWeekDates(year, kw)} {t('kw')} {kw} <ChevronRight className="nav-arrow" onClick={() => setKw(k => k + 1)} />
          </div>
          
          {!editingProjectId && (
            <button className="top-btn yellow-pdf" onClick={() => navigate(`/admin/weekly-export?kw=${kw}&year=${year}`)}>
              <FileText size={18} /> {t('weekly_plan_pdf')}
            </button>
          )}

          <button 
            className={`top-btn green ${hasUnsavedChanges ? "unsaved-glow" : ""} ${projectDone ? "done" : ""}`} 
            onClick={handleFinishProject} 
            disabled={creating}
            style={{ opacity: (hasUnsavedChanges || editingProjectId) ? 1 : 0.6, cursor: (hasUnsavedChanges || editingProjectId) ? "pointer" : "not-allowed" }}
          >
            {creating ? "..." : (
               <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {hasUnsavedChanges || editingProjectId ? <Unlock size={18} /> : <Lock size={18} />}
                  {projectDone ? t('sent') : (editingProjectId || hasUnsavedChanges) ? t('update') : t('create_and_send')}
               </div>
            )}
          </button>
          {editingProjectId && <button className="top-btn grey" onClick={handleCancelEdit}>{t('cancel_edit')}</button>}
        </div>

        <div className="weekly-grid">
          <div className="grid-header">
            <div className="cell header-worker-cell" onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}><span>{t('worker')}</span><ArrowUpDown size={14} className="worker-arrow"/></div>
            {DAYS.map((d, idx) => (<div key={d} className="cell">{t(DAYS_MAP[d])} ({getFormattedDate(year, kw, idx)})</div>))}
          </div>
          <div className="grid-body-scroll-container">
            {filtered.map((w, i) => (
              <div key={w.id} className="grid-row">
                <div className="cell worker-name">{i + 1}. {w.name}</div>
                {Array.from({ length: 7 }).map((_, d) => {
                  const st = dayStatuses[w.id]?.[d]; 
                  const ass = getAssignedProjectNames(w.id, d);
                  
                  let style = {};
                  let displayText = "";

                  if (st) {
                      if (st.label === "DELETED") {
                        style = { background: "rgba(255, 59, 59, 0.2)", border: "1px dashed #ff3b3b", color: "transparent" };
                      } else {
                        style = { 
                            background: st.color, 
                            border: `1px solid ${st.color}`, 
                            color: "#1a1a1a",
                            fontWeight: "bold",
                            fontSize: "13px"
                        };
                        displayText = st.i18nKey ? t(st.i18nKey) : st.label;
                      }
                  } else if (ass) {
                      style = { background: "#2d3748", border: "1px solid #4a5568", color: "#e2e8f0" };
                      displayText = ass;
                  }
                  
                  return (
                    <div key={d} className="cell day-cell" onClick={() => handleCellClick(w.id, d)} style={style}>
                        <span className="status-label">{displayText}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="sent-projects-area">
          <h3 className="area-title">{t('sent_projects')} ({sentProjects.length}):</h3>
          <div className="projects-scroll-wrapper">
             <div className="projects-grid-list">
               {sentProjects.map(p => (
                 <div key={p.id} className={`mini-project-card ${editingProjectId === p.id ? "editing" : ""}`}>
                   <div className="p-text">
                       <div className="p-name">{p.info.bauvorhaben}</div>
                       <div className="p-addr">{p.info.adresse}</div>
                   </div>
                   <div className="p-actions">
                     <button onClick={() => handleEditProject(p)} className="edit-btn-fixed"><Edit3 size={18}/></button>
                     <button onClick={() => { setViewProject(p); setShowViewModal(true); setIndividualEdits({}); setIsModalEditMode(false); }} className="eye-btn-fixed"><Eye size={18}/></button>
                     <button onClick={async () => { if(window.confirm(t('sureDelete'))) await deleteDoc(doc(db, "projects", p.id)); }} className="trash-btn-fixed"><Trash2 size={18}/></button>
                   </div>
                 </div>
               ))}
             </div>
          </div>
        </div>
      </div>

      <StatusModal 
        open={showStatusModal} 
        onClose={() => setShowStatusModal(false)} 
        onSelect={(val) => { 
          if (!val) return;
          if (val === "reset") { setPendingStatus(null); setApplyMode("reset"); } 
          else if (typeof val === 'string' && STATUS_MAP[val]) { setPendingStatus(STATUS_MAP[val]); setApplyMode("status"); }
          else if (typeof val === 'object' && val.label) { setPendingStatus(val); setApplyMode("status"); }
          setShowStatusModal(false); 
        }} 
      />
      <BudgetModal show={showBudgetModal} onClose={() => setShowBudgetModal(false)} info={info} setInfo={setInfo} t={t} />
      <ProjectViewModal 
        show={showViewModal} onClose={() => setShowViewModal(false)} project={viewProject} 
        targetKw={targetKw} setTargetKw={setTargetKw} copyWorkers={copyWorkers} setCopyWorkers={setCopyWorkers} 
        handleCopyToWeek={handleCopyToWeek} isModalEditMode={isModalEditMode} setIsModalEditMode={setIsModalEditMode} 
        individualEdits={individualEdits} handleIndividualChange={handleIndividualChange} saveIndividualChanges={saveIndividualChanges} t={t} 
      />
      <DeletionModal show={showDeletionModal} onClose={() => setShowDeletionModal(false)} workerName={deletionData?.workerName} projects={deletionData?.projects || []} onDelete={handleRemoveFromProject} t={t} />
    
      {warningMsg && (
        <div 
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => setWarningMsg(null)}
        >
          <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '12px', padding: '25px', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 0 20px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}><AlertCircle size={40} color="#ff3b3b" /></div>
            <h3 style={{ margin: '0 0 10px', color: 'white' }}>{t('attention') || "Dėmesio!"}</h3>
            <p style={{ color: '#ccc', marginBottom: '20px', lineHeight: '1.5' }}>{warningMsg}</p>
            <button onClick={() => setWarningMsg(null)} style={{ background: '#238636', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', width: '100%' }}>OK</button>
          </div>
        </div>
      )}

    </div>
  );
}